import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const QuizCompletionDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<any>(null);

  const handleComplete = async () => {
    setLoading(true);
    setReport(null);
    
    try {
      toast.info('🚀 Starting automated quiz completion...');
      
      const { data, error } = await supabase.functions.invoke('complete-quiz-generation');
      
      if (error) throw error;
      
      setReport(data);
      
      if (data.success) {
        toast.success('🎉 Quiz generation complete!', {
          description: data.message
        });
      } else {
        toast.warning('⚠️ Partial completion', {
          description: data.message
        });
      }
    } catch (error: any) {
      console.error('Error:', error);
      toast.error('❌ Generation failed', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-2xl font-bold mb-4">Quiz Generation Completion</h2>
      
      <p className="text-muted-foreground mb-6">
        Automatically generate all missing quizzes with zero manual intervention.
        The system will:
      </p>
      
      <ul className="list-disc ml-6 mb-6 space-y-2 text-sm">
        <li>Identify all modules without quizzes</li>
        <li>Generate quizzes using AI (with template fallback)</li>
        <li>Clean up stale failure records</li>
        <li>Provide a detailed completion report</li>
      </ul>
      
      <Button 
        onClick={handleComplete}
        disabled={loading}
        size="lg"
        className="w-full"
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {loading ? 'Generating Quizzes...' : 'Complete All Quizzes Automatically'}
      </Button>
      
      {report && (
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">Generation Report</h3>
            <div className="space-y-1 text-sm">
              <p>✅ Generated: {report.statistics?.generated || 0} quizzes</p>
              <p>📊 Coverage: {report.statistics?.coveragePercent || 0}%</p>
              <p>⏳ Remaining: {report.statistics?.remaining || 0} modules</p>
              <p>🧹 Cleaned: {report.cleanedStaleRecords || 0} stale records</p>
            </div>
          </div>
          
          {report.missingModules && report.missingModules.length > 0 && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg">
              <h3 className="font-semibold mb-2 text-amber-900 dark:text-amber-100">
                Modules Still Missing Quizzes
              </h3>
              <ul className="text-sm space-y-1">
                {report.missingModules.map((m: any) => (
                  <li key={m.module_id} className="text-amber-800 dark:text-amber-200">
                    • {m.module_title} ({m.course_id})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
